import React, { useContext, useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import MuiSnackbar from '../../common/MuiSnackbar';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import ButtonContainer from './ButtonContainer';
import SeoHelmet from '../../common/SeoHelmet';
import LabelContainer from './LabelContainer';
import InputLayer from './InputLayer';
import Vs30SoilProfileSvg from './Vs30SoilProfileSvg';
import { Context } from '../../../App';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

const MAX_Z2_M = 40;

const LayeredSoil = () => {
  const { user } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    resultsArray,
    calculations,
    lastData,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR REQUIRED INPUT VARIABLES
  const [name, setName] = useState('');
  const [layers, setLayers] = useState([]);
  const [svgLayers, setSvgLayers] = useState([]);

  //SET STATES FOR OPTIONAL INPUT VARIABLES
  const [surfaceAlluviumThicknessM, setSurfaceAlluviumThicknessM] = useState('');
  const [surfaceAlluviumVsAvg, setSurfaceAlluviumVsAvg] = useState('');
  const [stiffBaseVs, setStiffBaseVs] = useState('');
  const [stiffBasePresent, setStiffBasePresent] = useState('');
  const [softClayThicknessM, setSoftClayThicknessM] = useState('');
  const [plasticityIndexPI, setPlasticityIndexPI] = useState('');
  const [waterContentW, setWaterContentW] = useState('');
  const [liquefiablePresent, setLiquefiablePresent] = useState('');
  const [sensitiveClayPresent, setSensitiveClayPresent] = useState('');

  //SHOW MORE/LESS FOR INFO ALERTS
  const [openShowMore, setOpenShowMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  const sketchContainerRef = useRef(null);
  // SET THE LOADED INPUTS (FROM LOADS FROM MY CALCULATIONS)
  const setInputs = (inputs) => {
    //Required inputs
    setName(inputs.name);
    setLayers(inputs.layers);
    //Optional inputs
    setSurfaceAlluviumThicknessM(inputs.surfaceAlluviumThicknessM);
    setSurfaceAlluviumVsAvg(inputs.surfaceAlluviumVsAvg);
    setStiffBaseVs(inputs.stiffBaseVs);
    setStiffBasePresent(inputs.stiffBasePresent);
    setSoftClayThicknessM(inputs.softClayThicknessM);
    setPlasticityIndexPI(inputs.plasticityIndexPI);
    setWaterContentW(inputs.waterContentW);
    setLiquefiablePresent(inputs.liquefiablePresent);
    setSensitiveClayPresent(inputs.sensitiveClayPresent);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    //Required inputs
    name,
    layers,
    //Optional inputs
    surfaceAlluviumThicknessM,
    surfaceAlluviumVsAvg,
    stiffBaseVs,
    stiffBasePresent,
    softClayThicknessM,
    plasticityIndexPI,
    waterContentW,
    liquefiablePresent,
    sensitiveClayPresent,
  };

  //ADD LAYER
  const addLayer = (e) => {
    e.preventDefault();
    const layer = {
      id: layers.length + 1,
      z1: '',
      z2: '',
      th: '',
      Vs: '',
    };

    const newLayersFront = layers.slice();
    newLayersFront.push(layer);
    setLayers(newLayersFront);
  };

  //REMOVE LAYER
  const removeLayer = (e) => {
    e.preventDefault();
    const newLayersFront = layers.slice();
    newLayersFront.pop();
    setLayers(newLayersFront);
  };

  // Disable Calculate/Save when name/layers invalid or during loading
  useEffect(() => {
    const nameMissing = !name || !name.trim();
    const noLayers = !Array.isArray(layers) || layers.length === 0;

    let layersInvalid = false;

    if (!noLayers) {
      const isEmpty = (v) => v === '' || v == null; // treat "" / null / undefined as missing

      for (const L of layers) {
        const z1 = L?.z1;
        const z2 = L?.z2;
        const Vs = L?.Vs;

        // missing fields
        if (isEmpty(z1) || isEmpty(z2) || isEmpty(Vs)) {
          layersInvalid = true;
          break;
        }

        const nZ1 = Number(z1);
        const nZ2 = Number(z2);
        const nVs = Number(Vs);

        // non-numeric
        if (!Number.isFinite(nZ1) || !Number.isFinite(nZ2) || !Number.isFinite(nVs)) {
          layersInvalid = true;
          break;
        }

        // rules: z1 ≥ 0 allowed; z2 must be > z1 and <= max; Vs > 0
        if (!(nZ1 >= 0) || !(nZ2 > nZ1) || !(nZ2 <= MAX_Z2_M) || !(nVs > 0)) {
          layersInvalid = true;
          break;
        }
      }
    }

    const shouldDisable = nameMissing || noLayers || layersInvalid || isLoadingCalculate || isLoadingLoad || isLoadingSave;

    if (shouldDisable !== calculateButtonDisabled) setCalculateButtonDisabled(shouldDisable);
    if (shouldDisable !== saveButtonDisabled) setSaveButtonDisabled(shouldDisable);
  }, [
    name,
    layers,
    isLoadingCalculate,
    isLoadingLoad,
    isLoadingSave,
    calculateButtonDisabled,
    saveButtonDisabled,
    setCalculateButtonDisabled,
    setSaveButtonDisabled,
  ]);

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/vs30SiteClassEc8',
    LOADONE: `/api/v1/calculations/vs30SiteClassEc8/loadOne/`,
    LOADALL: '/api/v1/calculations/vs30SiteClassEc8/loadAll',
    UPDATE: `/api/v1/calculations/vs30SiteClassEc8/update/`,
    SAVE: '/api/v1/calculations/vs30SiteClassEc8/save',
    CHECKUNIQUENAME: `/api/v1/calculations/vs30SiteClassEc8/checkUniqueName/`,
    DELETE: '/api/v1/calculations/vs30SiteClassEc8/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  //TABS
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  const [showVs30CalcDetails, setShowVs30CalcDetails] = useState(false);
  const hasLayerOrderError = Array.isArray(layers)
    ? layers.some((layer) => {
        if (layer?.z1 === '' || layer?.z1 == null || layer?.z2 === '' || layer?.z2 == null) return false;
        const z1 = Number(layer?.z1);
        const z2 = Number(layer?.z2);
        if (!Number.isFinite(z1) || !Number.isFinite(z2)) return false;
        return z2 <= z1;
      })
    : false;

  useEffect(() => {
    if (!hasLayerOrderError) {
      setSvgLayers(layers);
    }
  }, [layers, hasLayerOrderError]);

  const outputs = result?.outputs || {};
  const canUserSeeCalculationSteps = Boolean(outputs?.canUserSeeCalculationSteps);
  const calculationDetails = outputs?.calculationDetails;
  const detailSegments = Array.isArray(calculationDetails?.segments) ? calculationDetails.segments : [];
  const sumHOverVs = Number(calculationDetails?.sumHOverVs);
  const vs30FromDetails = Number(calculationDetails?.vs30);
  const sumTermsText = detailSegments.map((segment) => Number(segment.hOverVs).toFixed(5)).join(' + ');

  const handleExportPdf = async () => {
    if (isExporting || !showOutput) return;
    if (user?.role === 'basic' || user?.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const [sketchPngBase64] = await Promise.all([
        svgContainerToPngBase64(sketchContainerRef.current, {
          scale: 2,
        }),
      ]);

      const payload = {
        ...calculationInput,
        sketchPngBase64,
      };

      const res = await fetch('/api/v1/pdf/vs30SiteClassEc8', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let serverMessage = '';
        try {
          const errorJson = await res.json();
          serverMessage = errorJson?.message || errorJson?.error || JSON.stringify(errorJson);
        } catch (e) {
          serverMessage = await res.text();
        }
        throw new Error(`PDF generation failed (${res.status}). ${serverMessage || 'No server message.'}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-vs30-site-class-ec8-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showMuiSnack(err.message || 'PDF generation failed. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Vs₃₀ & Site Class (EC8)"
        description="Determine Vs₃₀ and Eurocode 8 ground type (A–E, S1, S2) from layered Vₛ data. Includes optional checks for Type E alluvium, soft clays (S1), and S2 conditions."
        url="https://www.geocalc.app/calculations/vs30SiteClassEc8"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Vs₃₀ & Site Class (EC8)', url: 'https://www.geocalc.app/calculations/vs30SiteClassEc8' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user?.role} />
        <div className="right-calculation vs30SiteClassEc8Page">
          <section className="input vs30SiteClassEc8Input">
            <h1>Determine ground type using shear wave velocity according to Eurocode 8</h1>
            {isLoadingLoad && (
              <div className="loadingAbsolute">
                <CircularProgress size={30} />
              </div>
            )}
            <Box sx={{ width: '100%', bgcolor: 'background.paper', marginBottom: '20px' }}>
              <Tabs value={value} onChange={handleChange} centered>
                <Tab label="Soil Layers (Required)" sx={{ textTransform: 'none' }} />
                <Tab label="Type E (Optional)" sx={{ textTransform: 'none' }} />
                <Tab label="Type S1 (Optional)" sx={{ textTransform: 'none' }} />
                <Tab label="Type S2 (Optional)" sx={{ textTransform: 'none' }} />
              </Tabs>
            </Box>

            {value === 0 && (
              <>
                <div
                  className="vs30LayerContainer"
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
                  >
                    <Collapse in={openShowMore} collapsedSize={20}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          textAlign: 'justify',
                          lineHeight: 1.6,
                          letterSpacing: '0.3px',
                        }}
                      >
                        Provide a <strong>calculation name</strong> and define the
                        <strong> soil profile</strong> by specifying the depth and
                        <strong> shear wave velocity (Vₛ)</strong> of each layer from the ground surface downward. The tool will
                        automatically calculate the
                        <strong> average shear wave velocity (Vₛ₃₀)</strong> for the top 30 meters and classify the site into one
                        of the standard
                        <strong> ground types (A–D)</strong> defined in
                        <strong> Eurocode 8</strong>.
                        <br />
                        <br />
                        Additional optional inputs are available in the next tabs:
                        <strong> Type E</strong> for soft surface alluvium over stiff base,
                        <strong> Type S1</strong> for soft cohesive soils, and
                        <strong> Type S2</strong> for liquefiable or sensitive clays. Providing these optional parameters allows
                        the tool to detect
                        <strong> site-specific ground types</strong> where applicable.
                      </Typography>
                    </Collapse>
                    <Button size="small" onClick={() => setOpenShowMore((v) => !v)}>
                      {openShowMore ? 'Show less' : 'Show more'}
                    </Button>
                  </Alert>
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <TextField
                      sx={{
                        width: 150,
                        textAlign: 'left',
                        '& .MuiInputBase-input': {
                          textAlign: 'left', // ensures input text aligns left
                        },
                        '& .MuiInputLabel-root': {
                          textAlign: 'left', // ensures label aligns left
                          transformOrigin: 'left', // label animation starts from left
                        },
                        minWidth: 75,
                        maxWidth: 150,
                      }}
                      size="small"
                      label="Name"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                              arrow
                              placement="bottom"
                              TransitionComponent={Zoom}
                            >
                              <InfoTwoToneIcon fontSize={'string'} />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        maxLength: `${MAX_NAME_LENGTH}`,
                      }}
                      value={name}
                      onChange={handleChangeName}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    />
                    {hasLayerOrderError && (
                      <Typography variant="caption" color="error" sx={{ textAlign: 'left' }}>
                        Fix highlighted z<sub>2</sub> values: each z<sub>2</sub> must be greater than z<sub>1</sub>.
                      </Typography>
                    )}
                  </Box>
                  {<LabelContainer />}
                  {layers?.map((layer, index) => {
                    if (index === 0) {
                      layers[index].z1 = 0;
                    }
                    if (index !== layers.length - 1) {
                      layers[index + 1].z1 = layers[index].z2;
                    }
                    layer.th = layer.z2 - layer.z1;
                    const updateLayer = (key, value) => {
                      const newLayer = {
                        ...layer,
                      };
                      newLayer[key] = value;
                      const newLayers = layers.slice();
                      newLayers.splice(index, 1, newLayer);
                      setLayers(newLayers);
                    };
                    return (
                      <InputLayer
                        key={index}
                        layer={layer}
                        updateLayer={updateLayer}
                        isLoadingCalculate={isLoadingCalculate}
                        isLoadingLoad={isLoadingLoad}
                        isLoadingSave={isLoadingSave}
                      />
                    );
                  })}
                </div>
                <div className="Vs30SiteClassEc8ButtonContainer">
                  <Button
                    variant="outlined"
                    onClick={removeLayer}
                    disabled={isLoadingCalculate || isLoadingSave || isLoadingLoad || layers.length < 1}
                  >
                    Remove Layer
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={addLayer}
                    disabled={
                      isLoadingCalculate ||
                      isLoadingSave ||
                      isLoadingLoad ||
                      hasLayerOrderError ||
                      layers.length > 39 ||
                      Number(layers?.[layers.length - 1]?.z2) >= MAX_Z2_M
                    }
                  >
                    Add Layer
                  </Button>
                </div>
                <Vs30SoilProfileSvg layers={svgLayers} />
                <div
                  ref={sketchContainerRef}
                  style={{
                    position: 'absolute',
                    left: -99999,
                    top: -99999,
                    width: 800,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                  }}
                >
                  <Vs30SoilProfileSvg layers={svgLayers} />
                </div>
              </>
            )}
            {value === 1 && (
              <div className="vs30OptionalInfo">
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Collapse in={openShowMore} collapsedSize={20}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        lineHeight: 1.6, // controls vertical spacing between lines
                        letterSpacing: '0.3px', // light horizontal spacing between characters
                        textAlign: 'justify', // makes text evenly aligned on both sides
                      }}
                    >
                      These optional inputs help identify <strong>Ground Type E</strong> conditions (Eurocode 8). If your site
                      includes a soft surface alluvium layer
                      <em>(5–20 m thick, Vₛ &lt; 360 m/s)</em> over a stiff or rock-like base
                      <em>(Vₛ ≥ 800 m/s)</em>, fill these fields to let the tool detect an <strong>E-condition</strong>{' '}
                      automatically.
                    </Typography>
                  </Collapse>
                  <Button size="small" onClick={() => setOpenShowMore((v) => !v)}>
                    {openShowMore ? 'Show less' : 'Show more'}
                  </Button>
                </Alert>
                <TextField
                  sx={{ width: 250 }}
                  size="small"
                  label="Alluvium Thickness (m)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Thickness of soft surface deposit (5–20 m typical). If multiple layers of alluvium are present, the sum of their thicknesses should be used."
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={surfaceAlluviumThicknessM}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Alluvium Thickness', setSurfaceAlluviumThicknessM)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  sx={{ width: 250 }}
                  size="small"
                  label="Average Alluvium Vₛ (m/s)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Mean shear wave velocity of that layer (< 360 m/s). If multiple layers of alluvium are present, the thickness-weighted average should be used."
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={surfaceAlluviumVsAvg}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Alluvium Thickness', setSurfaceAlluviumVsAvg)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  sx={{ width: 250 }}
                  size="small"
                  label="Stiff Base Vₛ (m/s)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Shear wave velocity of underlying base (≥ 800 m/s)."
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={stiffBaseVs}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Alluvium Thickness', setStiffBaseVs)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ width: 250 }}
                  label={'Stiff Base Present?'}
                  size="small"
                  select
                  value={stiffBasePresent}
                  onChange={(e) => setStiffBasePresent(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </div>
            )}
            {value === 2 && (
              <div className="vs30OptionalInfo">
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Collapse in={openShowMore} collapsedSize={20}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        textAlign: 'justify',
                        lineHeight: 1.6,
                        letterSpacing: '0.3px',
                      }}
                    >
                      These inputs help identify <strong>Ground Type S1</strong>, representing{' '}
                      <strong>soft cohesive soils</strong> that may require site-specific analysis. S1 conditions occur when soft
                      clays or silts have a <em>thickness ≥ 10 m</em>,<em>plasticity index &gt; 40</em>, and
                      <em> water content ≥ 40 %</em>.
                    </Typography>
                  </Collapse>
                  <Button size="small" onClick={() => setOpenShowMore((v) => !v)}>
                    {openShowMore ? 'Show less' : 'Show more'}
                  </Button>
                </Alert>
                <TextField
                  sx={{ width: 250 }}
                  size="small"
                  label="Soft Clay Thickness (m)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Total thickness of soft cohesive layer (≥ 10 m)."
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={softClayThicknessM}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Soft Clay Thickness', setSoftClayThicknessM)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  sx={{ width: 250 }}
                  size="small"
                  label="Plasticity Index, PI (%)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="PI > 40 triggers S1 condition." arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={plasticityIndexPI}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Plasticity Index, PI', setPlasticityIndexPI)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  sx={{ width: 250 }}
                  size="small"
                  label="Water Content, w (%)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Natural water content ≥ 40 % triggers S1."
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={waterContentW}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Water Content, w (%)', setWaterContentW)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 3 && (
              <div className="vs30OptionalInfo">
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Collapse in={openShowMore} collapsedSize={20}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        textAlign: 'justify',
                        lineHeight: 1.6,
                        letterSpacing: '0.3px',
                      }}
                    >
                      Use these flags to indicate special soil conditions such as
                      <strong> liquefiable layers</strong> or
                      <strong> sensitive clays</strong>. When either is present, the site is classified as
                      <strong> Ground Type S2</strong>, which requires a <strong>site-specific seismic study </strong>
                      under Eurocode 8.
                    </Typography>
                  </Collapse>
                  <Button size="small" onClick={() => setOpenShowMore((v) => !v)}>
                    {openShowMore ? 'Show less' : 'Show more'}
                  </Button>
                </Alert>
                <TextField
                  style={{ width: 250 }}
                  label={'Liquefiable Soils Present?'}
                  size="small"
                  select
                  value={liquefiablePresent}
                  onChange={(e) => setLiquefiablePresent(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <TextField
                  style={{ width: 250 }}
                  label={'Sensitive Clays Present?'}
                  size="small"
                  select
                  value={sensitiveClayPresent}
                  onChange={(e) => setSensitiveClayPresent(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </div>
            )}
          </section>
          {value === 0 && (
            <ButtonContainer
              URL={URL}
              calculate={calculate}
              checkCalculationNameforSaving={checkCalculationNameforSaving}
              calculationInput={calculationInput}
              isLoadingCalculate={isLoadingCalculate}
              isLoadingLoad={isLoadingLoad}
              isLoadingSave={isLoadingSave}
              saveButtonDisabled={saveButtonDisabled}
              calculateButtonDisabled={calculateButtonDisabled}
              calculationCountReached={calculationCountReached}
              saveCountReached={saveCountReached}
            >
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={
                  calculateButtonDisabled || isLoadingCalculate || isLoadingLoad || isLoadingSave || !showOutput || isExporting
                }
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF...
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>
            </ButtonContainer>
          )}

          {isLoadingCalculate && (
            <div className="loadingAbsolute">
              <CircularProgress />
            </div>
          )}
          {showOutput && (
            <section className="Vs30SiteClassEc8ResultContainer ">
              <h2>Results </h2>
              <Alert
                severity="success"
                variant="outlined"
                sx={{
                  mb: 1.5,
                  pb: 0,
                  alignItems: 'flex-start',
                  '& .MuiAlert-message': { width: '100%' },
                  '& .MuiAlert-action': {
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'center',
                    margin: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                }}
                action={
                  canUserSeeCalculationSteps ? (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => setShowVs30CalcDetails((v) => !v)}
                      sx={{ width: 32, height: 32 }}
                    >
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: showVs30CalcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </IconButton>
                  ) : (
                    <Tooltip
                      title={
                        <span>
                          Upgrade to unlock calculation details.{' '}
                          <Link
                            href="/pricing"
                            target="_blank"
                            rel="noopener"
                            style={{ color: '#90caf9', textDecoration: 'underline' }}
                          >
                            See plans
                          </Link>
                        </span>
                      }
                      leaveDelay={500}
                      arrow
                    >
                      <LockOutlinedIcon
                        sx={{
                          fontSize: 20,
                          color: 'text.disabled',
                          cursor: 'not-allowed',
                          display: 'block',
                          mr: 1,
                        }}
                      />
                    </Tooltip>
                  )
                }
              >
                <Typography variant="body2" color="text.secondary">
                  <b>Calculation Details of Vs30</b>
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showVs30CalcDetails} unmountOnExit>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Vs₃₀ is computed by harmonic averaging over top 30 m:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <math display="block">
                        <msub>
                          <mi>V</mi>
                          <mn>s30</mn>
                        </msub>
                        <mo>=</mo>
                        <mfrac>
                          <mn>30</mn>
                          <mrow>
                            <munderover>
                              <mo>&sum;</mo>
                              <mi>i</mi>
                              <mi>n</mi>
                            </munderover>
                            <mfrac>
                              <msub>
                                <mi>h</mi>
                                <mi>i</mi>
                              </msub>
                              <msub>
                                <mi>V</mi>
                                <mi>s,i</mi>
                              </msub>
                            </mfrac>
                          </mrow>
                        </mfrac>
                      </math>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Used segments in top 30 m:
                    </Typography>
                    {detailSegments.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Detailed segment data is not available for this result.
                      </Typography>
                    )}
                    {detailSegments.length > 0 && (
                      <Box sx={{ mt: 1, overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>Segment</th>
                              <th style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>Layer</th>
                              <th style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>h (m)</th>
                              <th style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>Vs (m/s)</th>
                              <th style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>h/Vs (s)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailSegments.map((segment, idx) => (
                              <tr key={idx}>
                                <td style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>h{idx + 1}</td>
                                <td style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>
                                  {segment.extrapolated ? `Layer-${segment.layerId} (extrapolated)` : `Layer-${segment.layerId}`}
                                </td>
                                <td style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>
                                  {Number(segment.usedThickness).toFixed(2)}
                                </td>
                                <td style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>
                                  {Number(segment.Vs).toFixed(2)}
                                </td>
                                <td style={{ border: '1px solid #d0d0d0', padding: '6px', textAlign: 'center' }}>
                                  {Number(segment.hOverVs).toFixed(5)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Substituting each segment term:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Σ(h/Vs) = {sumTermsText || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      And from here, summing the terms:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Σ(h/Vs) = {Number.isFinite(sumHOverVs) ? sumHOverVs.toFixed(5) : '—'} s
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Substituting into the Vs₃₀ equation:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Vs₃₀ = 30 / {Number.isFinite(sumHOverVs) ? sumHOverVs.toFixed(5) : '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      And from here,
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Vs₃₀ = {Number.isFinite(vs30FromDetails) ? vs30FromDetails.toFixed(2) : '—'} m/s
                    </Typography>
                  </Collapse>
                )}
              </Alert>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1.5,
                    textAlign: 'justify',
                    lineHeight: 1.6,
                    letterSpacing: '0.3px',
                  }}
                >
                  The shear wave velocity avaraged over the top 30 meters (Vₛ₃₀) is equal to {}
                  <strong>{outputs?.vs30 ? outputs.vs30.toFixed(2) + ' m/s' : 'N/A'}</strong>. Based on this value and the
                  provided soil profile, the site is classified as{' '}
                  <strong>{outputs?.groundType ? outputs.groundType : 'N/A'}</strong> according to Eurocode 8 standards.
                </Typography>
              </Alert>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1.5,
                    textAlign: 'justify',
                    lineHeight: 1.6,
                    letterSpacing: '0.3px',
                  }}
                >
                  <strong>
                    Description of stratigraphic profile of ground type {outputs?.groundType ? outputs.groundType : 'N/A'}
                  </strong>
                  : {outputs?.groundTypeDesc ? outputs.groundTypeDesc : 'N/A'}
                </Typography>
              </Alert>
              <h2>Notes </h2>
              {(outputs?.notes || []).map((note, index) => (
                <Alert
                  key={index}
                  severity="success"
                  color="warning"
                  variant="outlined"
                  sx={{ mb: 1.5, pb: 0, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      textAlign: 'justify',
                      lineHeight: 1.6,
                      letterSpacing: '0.3px',
                    }}
                  >
                    {note}
                  </Typography>
                </Alert>
              ))}
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const LayeredSoilWithProvider = () => {
  return (
    <CalculationContextProvider>
      <LayeredSoil />
    </CalculationContextProvider>
  );
};
export default LayeredSoilWithProvider;
